import { useMemo, useRef, useState } from "react";
import { GripVertical, Trash2, ChevronDown, ChevronRight, Plus, Eye, EyeOff, Pencil, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import ImageUploader from "./ImageUploader";
import { CORE_SECTIONS, CUSTOM_SECTIONS, CALC_DEFAULTS, ensureLayout, coreMeta, customMeta } from "@/lib/layout";
import { uploadMediaFile } from "@/lib/media";
import { toast } from "sonner";

const uid = () => `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const CUSTOM_FORMS = {
  text: [
    { key: "overline", label: "Overline (small label)" },
    { key: "heading", label: "Heading" },
    { key: "body", label: "Body text (blank line = new paragraph)", type: "textarea", rows: 6 },
    { key: "dark", label: "Dark background", type: "switch" },
  ],
  text_image: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "body", label: "Body text", type: "textarea", rows: 5 },
    { key: "media", label: "Image", type: "image" },
    { key: "side", label: "Image side", type: "select", options: [["right", "Image right"], ["left", "Image left"]] },
  ],
  cards: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "columns", label: "Columns", type: "select", options: [["2", "2 columns"], ["3", "3 columns"], ["4", "4 columns"]] },
    { key: "items", label: "Cards", type: "pair_list", itemName: "card", pair: [
      { key: "media", label: "Image", type: "image" },
      { key: "title", label: "Title (e.g. Garden Suite)" },
      { key: "meta", label: "Side text (e.g. From ₹18,000 / night)" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "cta_text", label: "Button text (blank = no button)" },
      { key: "cta_url", label: "Button link (blank = opens enquiry form)" },
    ] },
  ],
  banner: [
    { key: "media", label: "Image / video", type: "image" },
    { key: "caption", label: "Caption (optional)" },
    { key: "height", label: "Height", type: "select", options: [["standard", "Standard"], ["tall", "Tall (near full screen)"]] },
  ],
  gallery_grid: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "columns", label: "Columns", type: "select", options: [["2", "2 columns"], ["3", "3 columns"], ["4", "4 columns"]] },
    { key: "items", label: "Images", type: "media_list" },
  ],
  video_embed: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "url", label: "Video URL (YouTube / Vimeo / .mp4)" },
  ],
  quote: [
    { key: "text", label: "Quote text", type: "textarea", rows: 4 },
    { key: "attribution", label: "Attribution (e.g. Priya & Arjun, December wedding)" },
  ],
  cta: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "body", label: "Supporting text", type: "textarea", rows: 3 },
    { key: "button_text", label: "Button text" },
    { key: "button_action", label: "Button action", type: "select", options: [["enquire", "Open enquiry form"], ["link", "Go to a link"], ["call", "Call a phone number"]] },
    { key: "button_url", label: "Link URL (if action is link)" },
    { key: "button_phone", label: "Phone number (if action is call)" },
    { key: "style", label: "Style", type: "select", options: [["dark", "Dark"], ["light", "Light"]] },
  ],
  stats: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "items", label: "Stats", type: "pair_list", itemName: "stat", pair: [{ key: "value", label: "Value (e.g. 40+)" }, { key: "label", label: "Label (e.g. Acres of estate)" }] },
  ],
  checklist: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "columns", label: "Columns", type: "select", options: [["2", "2 columns"], ["3", "3 columns"]] },
    { key: "items", label: "Items", type: "string_list", placeholder: "e.g. Dedicated bridal suite" },
  ],
  faq_extra: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "items", label: "Questions", type: "pair_list", itemName: "question", pair: [{ key: "q", label: "Question" }, { key: "a", label: "Answer", type: "textarea" }] },
  ],
  map: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "query", label: "Address or place name" },
  ],
  contact: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "address", label: "Address (each line = new line)", type: "textarea", rows: 3 },
    { key: "phone", label: "Phone (blank = site phone from Settings)" },
    { key: "email", label: "Email (blank = site email from Settings)" },
    { key: "map_query", label: "Google Maps — address or place name (blank = no map)" },
  ],
  calendar: [
    { key: "overline", label: "Overline (small label)" },
    { key: "heading", label: "Heading" },
    { key: "note", label: "Note shown beside calendar", type: "textarea", rows: 3 },
    { key: "booked", label: "Booked dates (click dates to mark as booked)", type: "dates" },
    { key: "calc", label: "Budget calculator", type: "budget_calc" },
  ],
  embed: [
    { key: "overline", label: "Overline" },
    { key: "heading", label: "Heading" },
    { key: "code", label: "HTML / embed code", type: "textarea", rows: 8, mono: true },
  ],
  divider: [
    { key: "style", label: "Style", type: "select", options: [["line", "Thin line"], ["space-sm", "Small space"], ["space-lg", "Large space"]] },
  ],
  album: [
    { key: "overline", label: "Overline (small label)" },
    { key: "heading", label: "Heading (e.g. Moments, chapter by chapter)" },
    { key: "intro", label: "Intro paragraph (optional)", type: "textarea", rows: 2 },
    { key: "categories", label: "Categories & photos", type: "album_cats" },
    { key: "enhance", label: "Auto-enhance photos (subtle colour, contrast & warmth boost — on screen and in the PDF)", type: "switch" },
    { key: "end_heading", label: "Closing page — heading (e.g. fin.)" },
    { key: "end_message", label: "Closing page — message (optional, appears in PDF)", type: "textarea", rows: 2 },
    { key: "end_footer", label: "Closing page — footer (e.g. happyretreats.in)" },
  ],
  team: [
    { key: "overline", label: "Overline (small label)" },
    { key: "heading", label: "Heading (e.g. Who takes care of your event)" },
    { key: "intro", label: "Intro paragraph (optional)", type: "textarea", rows: 2 },
    { key: "members", label: "Team members (no limit — add as many as you like)", type: "pair_list", itemName: "member", pair: [
      { key: "photo", label: "Photo (portrait works best)", type: "image" },
      { key: "name", label: "Name" },
      { key: "role", label: "Role (e.g. Event Manager)" },
      { key: "description", label: "Personality in 1–2 lines", type: "textarea", rows: 2 },
    ] },
  ],
  video_strip: [
    { key: "overline", label: "Overline (small label)" },
    { key: "heading", label: "Heading (e.g. See it in motion)" },
    { key: "intro", label: "Intro paragraph (optional)", type: "textarea", rows: 2 },
    { key: "items", label: "Videos (no limit — add as many as you like)", type: "pair_list", itemName: "video", pair: [
      { key: "url", label: "YouTube link (watch, share or Shorts URL)" },
      { key: "title", label: "Title shown under the video (optional)" },
    ] },
  ],
  decor_calc: [
    { key: "overline", label: "Overline (small label)" },
    { key: "heading", label: "Heading (e.g. Design your decor)" },
    { key: "intro", label: "Intro paragraph (optional)", type: "textarea", rows: 2 },
    { key: "base_label", label: "Base package name (e.g. Signature decor package)" },
    { key: "base_price", label: "Base package price in ₹ (e.g. 250000)" },
    { key: "base_includes", label: "What the base package includes (one per line)", type: "string_list", placeholder: "e.g. Floral mandap with seasonal flowers" },
    { key: "addons", label: "Add-on services (no limit)", type: "pair_list", itemName: "add-on", pair: [
      { key: "name", label: "Add-on name (e.g. Photo booth)" },
      { key: "price", label: "Unit price in ₹ (e.g. 15000)" },
      { key: "unit", label: "Unit label (optional, e.g. per unit / per table)" },
      { key: "description", label: "Short description (optional)" },
    ] },
    { key: "disclaimer", label: "Disclaimer (shown on page & PDF)", type: "textarea", rows: 2 },
  ],
  celebrations: [
    { key: "overline", label: "Overline (small label)" },
    { key: "heading", label: "Heading (e.g. Celebrations we host)" },
    { key: "intro", label: "Intro paragraph (optional)", type: "textarea", rows: 3 },
    { key: "style", label: "Display style", type: "select", options: [
      ["cards", "Cards (icon + title + description)"],
      ["chips", "Chips (compact, icon + title, wraps in rows)"],
      ["list", "List (single column, icon left, text right)"],
    ] },
    { key: "columns", label: "Cards per row (desktop)", type: "select", options: [["2", "2 columns"], ["3", "3 columns"], ["4", "4 columns"]] },
    { key: "items", label: "Celebrations", type: "pair_list", itemName: "celebration", pair: [
      { key: "icon", label: "Icon (paste an emoji, e.g. 💍 🎂 🎓 🎊)" },
      { key: "title", label: "Title (e.g. Weddings)" },
      { key: "description", label: "Description (1–2 lines, optional)", type: "textarea", rows: 2 },
    ] },
  ],
};

function StringList({ value = [], onChange, placeholder }) {
  return (
    <div className="space-y-2">
      {value.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input value={v} onChange={(e) => { const n = [...value]; n[i] = e.target.value; onChange(n); }} placeholder={placeholder} />
          <button type="button" onClick={() => onChange(value.filter((_, x) => x !== i))} className="px-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, ""])} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 rounded-md hover:bg-zinc-50"><Plus className="w-3 h-3" /> Add item</button>
    </div>
  );
}

function PairList({ value = [], onChange, pair, itemName = "item" }) {
  const update = (i, k, v) => { const n = [...value]; n[i] = { ...n[i], [k]: v }; onChange(n); };
  const cap = itemName.charAt(0).toUpperCase() + itemName.slice(1);
  return (
    <div className="space-y-3">
      {value.length === 0 && (
        <div className="border border-dashed border-zinc-300 rounded-md p-4 text-center text-sm text-zinc-500" data-testid="pair-list-empty">
          No {itemName}s yet. Click <b>Add {itemName}</b> below to add one — each {itemName} has its own image, text and button fields.
        </div>
      )}
      {value.map((it, i) => (
        <div key={i} className="bg-white border border-zinc-200 rounded-md p-3 space-y-2">
          <div className="flex justify-between text-xs text-zinc-500">{cap} {i + 1}<button type="button" onClick={() => onChange(value.filter((_, x) => x !== i))} className="text-red-600 hover:underline">Remove</button></div>
          {pair.map((f) => f.type === "textarea"
            ? <Textarea key={f.key} value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} placeholder={f.label} rows={3} />
            : f.type === "image"
            ? <ImageUploader key={f.key} label={f.label} value={it[f.key] || ""} onChange={(v) => update(i, f.key, v)} testId={`pair-img-${i}-${f.key}`} />
            : <Input key={f.key} value={it[f.key] || ""} onChange={(e) => update(i, f.key, e.target.value)} placeholder={f.label} />)}
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, {}])} className="text-sm inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-black" data-testid={`add-${itemName}-btn`}><Plus className="w-4 h-4" /> Add {itemName}</button>
    </div>
  );
}

function MediaList({ value = [], onChange }) {
  const bulkRef = useRef(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkPct, setBulkPct] = useState({ done: 0, total: 0 });

  const onBulk = async (e) => {
    const files = Array.from(e.target.files || []);
    if (bulkRef.current) bulkRef.current.value = "";
    if (!files.length) return;
    setBulkRunning(true);
    setBulkPct({ done: 0, total: files.length });
    const added = [];
    let failed = 0;
    for (let i = 0; i < files.length; i++) {
      try {
        const url = await uploadMediaFile(files[i]);
        added.push(url);
      } catch (_) { failed += 1; }
      setBulkPct({ done: i + 1, total: files.length });
    }
    setBulkRunning(false);
    if (added.length) {
      onChange([...value, ...added]);
      if (failed === 0) toast.success(`${added.length} image${added.length === 1 ? "" : "s"} added`);
      else toast.warning(`${added.length} added, ${failed} failed`);
    } else if (failed) {
      toast.error(`Upload failed for all ${failed} files`);
    }
  };

  return (
    <div className="space-y-3">
      {value.map((m, i) => (
        <div key={i} className="bg-white border border-zinc-200 rounded-md p-3">
          <div className="flex justify-between text-xs text-zinc-500 mb-2">Image {i + 1}<button type="button" onClick={() => onChange(value.filter((_, x) => x !== i))} className="text-red-600 hover:underline">Remove</button></div>
          <ImageUploader label="" value={m} onChange={(v) => { const n = [...value]; n[i] = v; onChange(n); }} testId={`layout-media-${i}`} />
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => onChange([...value, ""])} className="text-sm inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-black" data-testid="add-image-btn"><Plus className="w-4 h-4" /> Add image</button>
        <input ref={bulkRef} type="file" accept="image/*" multiple className="hidden" onChange={onBulk} data-testid="media-list-bulk-input" />
        <button type="button" onClick={() => bulkRef.current?.click()} disabled={bulkRunning} className="text-sm inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-zinc-300 rounded-md hover:bg-zinc-50 disabled:opacity-60" data-testid="media-list-bulk-btn">
          {bulkRunning ? `Uploading ${bulkPct.done}/${bulkPct.total}…` : "Upload multiple"}
        </button>
      </div>
    </div>
  );
}

function DatesPicker({ value = [], onChange }) {
  const selected = useMemo(() => value.map((s) => new Date(`${s}T00:00:00`)), [value]);
  return (
    <div className="bg-white border border-zinc-200 rounded-md inline-block">
      <Calendar mode="multiple" selected={selected} onSelect={(days) => onChange((days || []).map(toISO).sort())} />
      <div className="px-3 pb-3 text-xs text-zinc-500">{value.length} date{value.length === 1 ? "" : "s"} marked as booked</div>
    </div>
  );
}

function AlbumCategoriesEditor({ value = [], onChange }) {
  const cats = value;
  const setCat = (i, patch) => onChange(cats.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= cats.length) return;
    const n = [...cats];
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };
  return (
    <div className="space-y-3">
      <div className="text-[11px] text-zinc-400">Rename, reorder, add or remove categories freely (Haldi, Mehendi, Sangeeth…). Each category becomes a chapter in the album &amp; the PDF.</div>
      {cats.map((c, i) => (
        <details key={i} className="border border-zinc-200 rounded-md bg-white" data-testid={`album-cat-${i}`}>
          <summary className="flex items-center gap-2 p-3 cursor-pointer list-none">
            <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />
            <Input value={c.name || ""} onChange={(e) => setCat(i, { name: e.target.value })} onClick={(e) => e.preventDefault()} placeholder="Category name (e.g. Haldi)" className="flex-1" data-testid={`album-cat-name-${i}`} />
            <span className="text-xs text-zinc-400 whitespace-nowrap">{(c.images || []).filter(Boolean).length} photos</span>
            <button type="button" onClick={(e) => { e.preventDefault(); move(i, -1); }} className="text-zinc-400 hover:text-zinc-700 px-1" aria-label="Move up">↑</button>
            <button type="button" onClick={(e) => { e.preventDefault(); move(i, 1); }} className="text-zinc-400 hover:text-zinc-700 px-1" aria-label="Move down">↓</button>
            <button type="button" onClick={(e) => { e.preventDefault(); onChange(cats.filter((_, j) => j !== i)); }} className="text-zinc-400 hover:text-red-600 px-1" aria-label="Remove category" data-testid={`album-cat-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </summary>
          <div className="p-3 pt-0 border-t border-zinc-100 space-y-3">
            <div>
              <label className="text-xs text-zinc-500">Opening message (shown on this chapter&apos;s title page)</label>
              <Textarea value={c.intro_text || ""} onChange={(e) => setCat(i, { intro_text: e.target.value })} rows={2} placeholder="e.g. Where the laughter began — turmeric, marigolds and mischief." data-testid={`album-cat-intro-${i}`} />
            </div>
            <MediaList value={(c.images || [])} onChange={(v) => setCat(i, { images: v })} />
            <div>
              <label className="text-xs text-zinc-500">Closing message (shown after this chapter&apos;s photos)</label>
              <Textarea value={c.outro_text || ""} onChange={(e) => setCat(i, { outro_text: e.target.value })} rows={2} placeholder="e.g. And just like that, the yellow evening melted into music." data-testid={`album-cat-outro-${i}`} />
            </div>
          </div>
        </details>
      ))}
      <button type="button" onClick={() => onChange([...cats, { name: "", images: [] }])} className="text-sm inline-flex items-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-black" data-testid="album-add-category">
        <Plus className="w-4 h-4" /> Add category
      </button>
    </div>
  );
}

function BudgetCalcEditor({ value, onChange }) {
  const calc = value && typeof value === "object" ? value : {};
  const enabled = !!calc.enabled;
  const set = (patch) => onChange({ ...calc, ...patch });
  const enable = (v) => {
    if (v) onChange({ ...JSON.parse(JSON.stringify(CALC_DEFAULTS)), ...calc, enabled: true });
    else onChange({ ...calc, enabled: false });
  };
  return (
    <div className="border border-zinc-200 rounded-md p-4 bg-white space-y-3">
      <label className="flex items-center gap-3 text-sm">
        <Switch checked={enabled} onCheckedChange={enable} data-testid="calc-enable-toggle" />
        <span className="font-medium">Wedding budget calculator</span>
      </label>
      <div className="text-xs text-zinc-500">When guests select their dates on the calendar, an interactive budget calculator opens below it with venue, food and payment-discount pricing, and lets them enquire with the estimate.</div>
      {enabled && (
        <div className="space-y-5 pt-2">
          <div>
            <label className="text-xs font-medium text-zinc-700">Venue packages (by duration)</label>
            <div className="text-[11px] text-zinc-400 mb-2">The package matching the number of selected days is applied automatically.</div>
            <PairList value={calc.venue_packages || []} onChange={(v) => set({ venue_packages: v })} itemName="venue package" pair={[
              { key: "label", label: "Label (e.g. 24 hours)" },
              { key: "days", label: "Days (e.g. 1)" },
              { key: "price", label: "Venue price incl. GST (e.g. 650000)" },
            ]} />
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700">Food packages (per head, per day)</label>
            <div className="text-[11px] text-zinc-400 mb-2">Min guarantee pax × rate × days, plus additional guests at the additional rate.</div>
            <PairList value={calc.food_packages || []} onChange={(v) => set({ food_packages: v })} itemName="food package" pair={[
              { key: "label", label: "Label (e.g. 50 Pax Package)" },
              { key: "min_pax", label: "Minimum guarantee pax (e.g. 50)" },
              { key: "rate", label: "Rate per head/day (e.g. 3000)" },
              { key: "extra_rate", label: "Additional guest rate per head/day (e.g. 1850)" },
            ]} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              ["guest_min", "Guest slider — minimum"],
              ["guest_max", "Guest slider — maximum"],
              ["guest_default", "Default guest count"],
              ["extra_guest_threshold", "Extra stay charge above (guests)"],
              ["extra_guest_charge", "Extra stay charge ₹/person/day"],
            ].map(([k, lab]) => (
              <div key={k}>
                <label className="text-xs text-zinc-500">{lab}</label>
                <Input value={calc[k] ?? ""} onChange={(e) => set({ [k]: e.target.value })} data-testid={`calc-field-${k}`} />
              </div>
            ))}
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-700">Payment modes &amp; discounts</label>
            <div className="text-[11px] text-zinc-400 mb-2">Discount % is applied on venue + food. Extra stay charges are excluded from the discount.</div>
            <PairList value={calc.payment_modes || []} onChange={(v) => set({ payment_modes: v })} itemName="payment mode" pair={[
              { key: "label", label: "Label (e.g. 100% upfront)" },
              { key: "discount", label: "Discount % (e.g. 35)" },
              { key: "note", label: "Small note (e.g. confirms booking)" },
            ]} />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Disclaimer line (shown under the estimated total)</label>
            <Textarea value={calc.disclaimer || ""} onChange={(e) => set({ disclaimer: e.target.value })} rows={2} />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ f, data, setData }) {
  const v = data[f.key];
  const set = (val) => setData({ ...data, [f.key]: val });
  if (f.type === "textarea") return <div><label className="text-xs text-zinc-500">{f.label}</label><Textarea value={v || ""} onChange={(e) => set(e.target.value)} rows={f.rows || 4} className={f.mono ? "font-mono text-xs" : ""} /></div>;
  if (f.type === "select") return (
    <div><label className="text-xs text-zinc-500">{f.label}</label>
      <Select value={String(v ?? f.options[0][0])} onValueChange={set}>
        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>{f.options.map(([val, lab]) => <SelectItem key={val} value={val}>{lab}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  if (f.type === "switch") return <label className="flex items-center gap-2 text-sm"><Switch checked={!!v} onCheckedChange={set} /> {f.label}</label>;
  if (f.type === "image") return <ImageUploader label={f.label} value={v || ""} onChange={set} testId={`layout-field-${f.key}`} />;
  if (f.type === "media_list") return <div><label className="text-xs text-zinc-500">{f.label}</label><div className="mt-2"><MediaList value={v || []} onChange={set} /></div></div>;
  if (f.type === "string_list") return <div><label className="text-xs text-zinc-500">{f.label}</label><div className="mt-2"><StringList value={v || []} onChange={set} placeholder={f.placeholder} /></div></div>;
  if (f.type === "pair_list") return <div><label className="text-xs text-zinc-500">{f.label}</label><div className="mt-2"><PairList value={v || []} onChange={set} pair={f.pair} itemName={f.itemName || "item"} /></div></div>;
  if (f.type === "dates") return <div><label className="text-xs text-zinc-500">{f.label}</label><div className="mt-2"><DatesPicker value={v || []} onChange={set} /></div></div>;
  if (f.type === "budget_calc") return <div><label className="text-xs text-zinc-500">{f.label}</label><div className="mt-2"><BudgetCalcEditor value={v} onChange={set} /></div></div>;
  if (f.type === "album_cats") return <div><label className="text-xs text-zinc-500">{f.label}</label><div className="mt-2"><AlbumCategoriesEditor value={v || []} onChange={set} /></div></div>;
  return <div><label className="text-xs text-zinc-500">{f.label}</label><Input value={v || ""} onChange={(e) => set(e.target.value)} /></div>;
}

function SectionAlignEditor({ data, setData, defaultAlign = "left" }) {
  const val = data.align || defaultAlign;
  return (
    <div className="border-t border-zinc-200/70 pt-4 mt-2">
      <label className="text-xs text-zinc-500 mb-1.5 block">Content alignment</label>
      <div className="flex flex-wrap gap-2">
        {[
          { v: "left", label: "Left" },
          { v: "center", label: "Center" },
          { v: "right", label: "Right" },
        ].map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setData({ ...data, align: o.v })}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              val === o.v ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
            }`}
            data-testid={`section-align-${o.v}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-zinc-400 mt-1.5">Aligns the heading, intro and text content of this section.</div>
    </div>
  );
}

function SectionSpacingEditor({ data, setData }) {
  const val = data.spacing || "normal";
  return (
    <div className="border-t border-zinc-200/70 pt-4 mt-2">
      <label className="text-xs text-zinc-500 mb-1.5 block">Section spacing</label>
      <div className="flex flex-wrap gap-2">
        {[
          { v: "compact", label: "Compact" },
          { v: "normal", label: "Normal" },
          { v: "spacious", label: "Spacious" },
          { v: "none", label: "None" },
        ].map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setData({ ...data, spacing: o.v })}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              val === o.v ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
            }`}
            data-testid={`section-spacing-${o.v}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <div className="text-[10px] text-zinc-400 mt-1.5">Controls the vertical padding above and below this section. Default is Normal.</div>
    </div>
  );
}

function SectionCTAEditor({ data, setData }) {
  const cta = data.cta || {};
  const set = (patch) => setData({ ...data, cta: { ...cta, ...patch } });
  return (
    <div className="border-t border-zinc-200/70 pt-4 mt-2">
      <label className="flex items-center gap-3 text-sm">
        <Switch
          checked={!!cta.enabled}
          onCheckedChange={(v) => set({ enabled: v, label: cta.label || "Enquire now", action: cta.action || "enquire" })}
          data-testid="section-cta-toggle"
        />
        <span className="font-medium">Add a call-to-action button</span>
      </label>
      {cta.enabled && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 pl-1">
          <div>
            <label className="text-xs text-zinc-500">Button label</label>
            <Input value={cta.label || ""} onChange={(e) => set({ label: e.target.value })} placeholder="Enquire now" data-testid="section-cta-label" />
          </div>
          <div>
            <label className="text-xs text-zinc-500">On click</label>
            <select
              value={cta.action || "enquire"}
              onChange={(e) => set({ action: e.target.value })}
              className="w-full text-sm border border-zinc-300 rounded-md px-3 py-2 bg-white h-10"
              data-testid="section-cta-action"
            >
              <option value="enquire">Open the enquiry form</option>
              <option value="link">Go to a URL</option>
              <option value="call">Call a phone number</option>
            </select>
          </div>
          {cta.action === "call" && (
            <div className="md:col-span-2">
              <label className="text-xs text-zinc-500">Phone number</label>
              <Input value={cta.phone || ""} onChange={(e) => set({ phone: e.target.value })} placeholder="+91 90000 00000" data-testid="section-cta-phone" />
              <div className="text-[10px] text-zinc-400 mt-1">Visitors on phones will get a tap-to-call prompt.</div>
            </div>
          )}
          {cta.action === "link" && (
            <div className="md:col-span-2">
              <label className="text-xs text-zinc-500">Destination URL</label>
              <Input value={cta.url || ""} onChange={(e) => set({ url: e.target.value })} placeholder="/about-us or https://…" data-testid="section-cta-url" />
              <div className="text-[10px] text-zinc-400 mt-1">Starts with <b>/</b> for pages on this site; full <b>https://…</b> URLs open in a new tab.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SectionEditor({ sec, onData }) {
  if (sec.type.startsWith("core:")) {
    const meta = coreMeta(sec.type.slice(5));
    if (!meta) return null;
    return (
      <div className="space-y-3">
        {meta.fields.length === 0 && <div className="text-xs text-zinc-500">{meta.note || "No editable heading for this section."}</div>}
        {meta.fields.length > 0 && <div className="text-xs text-zinc-500">Leave blank to use the default text.</div>}
        {meta.fields.includes("overline") && (
          <div><label className="text-xs text-zinc-500">Overline <span className="text-zinc-400">(default: &ldquo;{meta.defaults.overline}&rdquo;)</span></label>
            <Input value={sec.data?.overline || ""} onChange={(e) => onData({ ...sec.data, overline: e.target.value })} placeholder={meta.defaults.overline} /></div>
        )}
        {meta.fields.includes("heading") && (
          <div><label className="text-xs text-zinc-500">Heading <span className="text-zinc-400">(default: &ldquo;{meta.defaults.heading}&rdquo;)</span></label>
            <Input value={sec.data?.heading || ""} onChange={(e) => onData({ ...sec.data, heading: e.target.value })} placeholder={meta.defaults.heading} /></div>
        )}
        {meta.note && <div className="text-xs text-zinc-400">{meta.note}</div>}
        <SectionCTAEditor data={sec.data || {}} setData={onData} />
      </div>
    );
  }
  const form = CUSTOM_FORMS[sec.type] || [];
  return (
    <div className="space-y-3">
      {form.map((f) => <Field key={f.key} f={f} data={sec.data || {}} setData={onData} />)}
      {sec.type !== "divider" && (
        <SectionAlignEditor data={sec.data || {}} setData={onData} defaultAlign={["quote", "cta"].includes(sec.type) ? "center" : "left"} />
      )}
      <SectionSpacingEditor data={sec.data || {}} setData={onData} />
      {sec.type !== "cta" && <SectionCTAEditor data={sec.data || {}} setData={onData} />}
    </div>
  );
}

export default function LayoutEditor({ layout, onChange, coreEnabled = true }) {
  const sections = useMemo(() => coreEnabled ? ensureLayout(layout) : (Array.isArray(layout) ? layout.map((s) => ({ enabled: true, data: {}, ...s })) : []), [layout, coreEnabled]);
  const [expanded, setExpanded] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragIdx = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [dragArm, setDragArm] = useState(null);

  const commit = (next) => { setDirty(true); onChange(next); };
  const updateSec = (id, patch) => commit(sections.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const onDragStart = (i) => { dragIdx.current = i; setDragging(i); };
  const onDragEnter = (i) => {
    if (dragIdx.current === null || dragIdx.current === i) return;
    const next = [...sections];
    const [moved] = next.splice(dragIdx.current, 1);
    next.splice(i, 0, moved);
    dragIdx.current = i;
    setDragging(i);
    commit(next);
  };
  const onDragEnd = () => { dragIdx.current = null; setDragging(null); setDragArm(null); };

  const addSection = (type) => {
    const meta = customMeta(type);
    const sec = { id: uid(), type, enabled: true, data: JSON.parse(JSON.stringify(meta.defaults)) };
    commit([...sections, sec]);
    setExpanded(sec.id);
    setPaletteOpen(false);
  };

  return (
    <div data-testid="layout-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-zinc-600 max-w-lg">
          Drag sections to reorder the page. Toggle the eye to show/hide. Click <b>Edit</b> to change section content.{coreEnabled ? " The hero always stays on top." : ""}
        </div>
        <Dialog open={paletteOpen} onOpenChange={setPaletteOpen}>
          <DialogTrigger asChild>
            <button className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md hover:bg-black" data-testid="add-section-btn">
              <Plus className="w-4 h-4" /> Add section
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add a section</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
              {CUSTOM_SECTIONS.map((c) => (
                <button key={c.type} onClick={() => addSection(c.type)} className="text-left border border-zinc-200 rounded-md p-4 hover:border-zinc-900 hover:bg-zinc-50 transition-colors" data-testid={`palette-${c.type}`}>
                  <div className="font-medium text-sm">{c.label}</div>
                  <div className="text-xs text-zinc-500 mt-1">{c.desc}</div>
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {dirty && (
        <div className="mt-4 flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm" data-testid="layout-dirty-banner">
          <AlertCircle className="w-4 h-4" />
          <span>You have unsaved layout changes. Click <b>Save</b> at the top-right to publish them.</span>
        </div>
      )}

      <div className="mt-6 space-y-2">
        {sections.map((sec, i) => {
          const isCore = sec.type.startsWith("core:");
          const label = isCore ? coreMeta(sec.type.slice(5))?.label : (customMeta(sec.type)?.label || sec.type);
          const sub = isCore ? "Built-in" : "Custom";
          const isOpen = expanded === sec.id;
          const displayTitle = sec.data?.heading || label;
          return (
            <div
              key={sec.id}
              draggable={dragArm === i}
              onDragStart={() => onDragStart(i)}
              onDragEnter={() => onDragEnter(i)}
              onDragOver={(e) => e.preventDefault()}
              onDragEnd={onDragEnd}
              className={`bg-white border rounded-md transition-shadow ${dragging === i ? "border-zinc-900 shadow-lg" : "border-zinc-200"} ${sec.enabled === false ? "opacity-60" : ""}`}
              data-testid={`layout-row-${sec.type.replace(":", "-")}`}
            >
              <div className="flex items-center gap-3 px-3 py-2.5">
                <GripVertical
                  className="w-4 h-4 text-zinc-400 cursor-grab shrink-0"
                  title="Drag to reorder"
                  onMouseDown={() => setDragArm(i)}
                  onMouseUp={() => setDragArm(null)}
                />
                <button type="button" onClick={() => setExpanded(isOpen ? null : sec.id)} className="flex items-center gap-2 flex-1 min-w-0 text-left hover:bg-zinc-50 -mx-1 px-1 py-1 rounded" data-testid={`layout-expand-${sec.type.replace(":", "-")}`}>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />}
                  <span className="text-sm font-medium truncate">{displayTitle}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${isCore ? "bg-zinc-100 text-zinc-600" : "bg-amber-100 text-amber-700"}`}>{isCore ? sub : (customMeta(sec.type)?.label || sub)}</span>
                </button>
                <button type="button" onClick={() => setExpanded(isOpen ? null : sec.id)} className={`shrink-0 inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border rounded transition-colors ${isOpen ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`} data-testid={`layout-edit-${sec.type.replace(":", "-")}`}>
                  <Pencil className="w-3.5 h-3.5" /> {isOpen ? "Close" : "Edit"}
                </button>
                <button type="button" onClick={() => updateSec(sec.id, { enabled: sec.enabled === false })} title={sec.enabled === false ? "Hidden — click to show" : "Visible — click to hide"} className="text-zinc-500 hover:text-zinc-900 shrink-0" data-testid={`layout-toggle-${sec.type.replace(":", "-")}`}>
                  {sec.enabled === false ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                {!isCore && (
                  <button type="button" onClick={() => { if (confirm("Remove this section?")) commit(sections.filter((s) => s.id !== sec.id)); }} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`layout-delete-${sec.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {isOpen && (
                <div className="border-t border-zinc-100 bg-zinc-50/60 px-4 py-4 rounded-b-md">
                  <SectionEditor sec={sec} onData={(data) => updateSec(sec.id, { data })} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-4 text-xs text-zinc-400">Remember to hit <b>Save</b> (top right) to publish your layout.</div>
    </div>
  );
}
